import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { ZONES } from "../data";

const ZoneRow = ({ zone, flip }) => (
  <motion.article
    data-testid={`zone-card-${zone.id}`}
    initial={{ opacity: 0, y: 80 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-120px" }}
    transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
    className={`group grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center py-16 md:py-24 border-t border-cream/10 ${
      flip ? "" : ""
    }`}
  >
    <div className={`lg:col-span-7 ${flip ? "lg:order-2" : ""}`}>
      <div className="relative overflow-hidden clip-frame border border-cream/10">
        <img
          src={zone.image}
          alt={zone.name}
          className="w-full h-[300px] md:h-[460px] object-cover image-zoom"
          data-testid={`zone-image-${zone.id}`}
        />
        <span className="absolute top-5 left-5 bg-night/80 backdrop-blur px-4 py-2 text-xs tracking-[0.25em] text-amber uppercase">
          Zone {zone.index}
        </span>
      </div>
    </div>
    <div className={`lg:col-span-5 ${flip ? "lg:order-1 lg:text-right lg:justify-self-end" : ""}`}>
      <p className="font-display text-6xl md:text-7xl text-stroke-cream leading-none mb-4">{zone.index}</p>
      <h3 className="font-display text-3xl md:text-5xl tracking-tight text-cream mb-2 group-hover:text-amber transition-colors duration-500">
        {zone.name}
      </h3>
      <p className="text-xs tracking-[0.3em] uppercase text-amber mb-6">{zone.tagline}</p>
      <p className={`text-base md:text-lg leading-relaxed text-cream/70 mb-8 max-w-md ${flip ? "lg:ml-auto" : ""}`}>
        {zone.description}
      </p>
      <ul className={`flex flex-wrap gap-3 ${flip ? "lg:justify-end" : ""}`} data-testid={`zone-activities-${zone.id}`}>
        {zone.activities.map((a) => (
          <li
            key={a}
            className="border border-amber/30 text-cream/80 text-xs tracking-[0.15em] uppercase px-4 py-2 hover:bg-amber hover:text-night transition-colors duration-300 cursor-default"
          >
            {a}
          </li>
        ))}
      </ul>
    </div>
  </motion.article>
);

export const Zones = () => (
  <section id="experiences" data-testid="zones-section" className="relative bg-night py-28 md:py-40">
    <div className="max-w-[1600px] mx-auto px-6 md:px-12">
      <motion.div
        initial={{ opacity: 0, y: 60 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10"
      >
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-amber mb-6" data-testid="zones-label">
            Experience Zones
          </p>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight text-cream leading-[1.05] max-w-2xl">
            Five worlds. <span className="italic text-amber">One wild estate.</span>
          </h2>
        </div>
        <button
          data-testid="zones-cta-enquire"
          onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
          className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-cream/70 hover:text-amber transition-colors duration-300 group"
        >
          Enquire about a zone
          <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
        </button>
      </motion.div>

      {ZONES.map((z, i) => (
        <ZoneRow key={z.id} zone={z} flip={i % 2 === 1} />
      ))}
    </div>
  </section>
);
